import { useEffect, useState, type KeyboardEvent } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const pad = (n: number) => String(n).padStart(2, "0");

function fromYmd(v: string | null | undefined): Date | undefined {
  if (!v) return undefined;
  const [y, m, d] = v.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

const toYmd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function formatUsDate(v: string | null | undefined): string {
  const d = fromYmd(v);
  return d ? `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}` : "";
}

function fullYear(y: string, preferPast: boolean): number {
  if (y.length === 4) return Number(y);
  const n = Number(y);
  const thisYear = new Date().getFullYear();
  if (preferPast) return 2000 + n > thisYear ? 1900 + n : 2000 + n;
  return n > (thisYear % 100) + 10 ? 1900 + n : 2000 + n;
}

export function parseTypedDate(text: string, allowShortYear = true, preferPast = false): string | null | undefined {
  const t = text.trim();
  if (!t) return null;
  let y: number, m: number, d: number;
  const iso = (allowShortYear ? /^(\d{4})-(\d{1,2})-(\d{1,2})(?:T[\d:.]+Z?)?$/ : /^(\d{4})-(\d{1,2})-(\d{2})$/).exec(t);
  const us = (allowShortYear ? /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})$/ : /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/).exec(t);
  const digits = /^(\d{2})(\d{2})(\d{4})$/.exec(t);
  if (iso) [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else if (us) [m, d, y] = [Number(us[1]), Number(us[2]), fullYear(us[3], preferPast)];
  else if (digits) [m, d, y] = [Number(digits[1]), Number(digits[2]), Number(digits[3])];
  else return undefined;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return undefined;
  return toYmd(date);
}

export function DateTextInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  readOnly,
  className,
  disableFuture,
  defaultYear,
}: {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  onBlur?: (committed: string | null) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  className?: string;
  disableFuture?: boolean;
  defaultYear?: number;
}) {
  const [text, setText] = useState(() => formatUsDate(value));
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const selected = fromYmd(value);
  const today = new Date();
  const thisYear = today.getFullYear();

  useEffect(() => {
    const current = (value ?? "").slice(0, 10) || null;
    setText((t) => (parseTypedDate(t) === current ? t : formatUsDate(value)));
  }, [value]);

  const current = (value ?? "").slice(0, 10) || null;

  const accept = (raw: string, allowShortYear = true): string | null | undefined => {
    const ymd = parseTypedDate(raw, allowShortYear, disableFuture);
    if (ymd === undefined) return undefined;
    if (ymd && disableFuture && ymd > toYmd(today)) return undefined;
    if (ymd !== current) onChange(ymd);
    return ymd;
  };

  return (
    <div className="relative">
      <Input
        type="text"
        inputMode="numeric"
        placeholder="MM/DD/YYYY"
        readOnly={readOnly}
        className={cn(!readOnly && "pr-9", className)}
        value={text}
        onChange={(e) => { setText(e.target.value); setProblem(null); accept(e.target.value, false); }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          const ymd = accept(text);
          if (ymd === undefined) {
            setProblem(disableFuture && parseTypedDate(text, true, true) ? "The date can't be in the future." : "Enter a real date as MM/DD/YYYY.");
            setText(formatUsDate(value));
            onBlur?.(current);
            return;
          }
          setText(formatUsDate(ymd));
          onBlur?.(ymd);
        }}
      />
      {!readOnly && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            type="button"
            aria-label="Pick a date"
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <CalendarIcon className="h-4 w-4" />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              captionLayout="dropdown"
              startMonth={new Date(1920, 0)}
              endMonth={new Date(disableFuture ? thisYear : thisYear + 10, 11)}
              defaultMonth={selected ?? (defaultYear != null ? new Date(defaultYear, 0) : undefined)}
              selected={selected}
              disabled={disableFuture ? { after: today } : undefined}
              onSelect={(d) => {
                onChange(d ? toYmd(d) : null);
                setProblem(null);
                setOpen(false);
              }}
              autoFocus
            />
          </PopoverContent>
        </Popover>
      )}
      {problem && <p className="mt-1 text-xs text-destructive">{problem}</p>}
    </div>
  );
}
